/**
 * @ngdoc service
 * @name SessionStorageCacheService
 * @description
 * A caching service supported by session storage.
 */
export const SessionStorageCacheService = function SessionStorageCacheService($window, $q, Settings) {
    const service = {};

    // 5 minutes expiration
    // we could consider defining a value per cache
    const CACHE_TTL_IN_SECONDS = 5 * 60; 
    const CACHE_PREFIX = 'buildium_cache_';

    const storage = $window.sessionStorage;
    
    function getUserCacheName(cacheName) {
        const version = Settings.CommonSettingsInfo.Version;
        const accountId = Settings.SubscriberSettingsInfo.AccountId;
        const userId = Settings.UserSettingsInfo.UserId;

        return `${CACHE_PREFIX}_${version}_${cacheName}_${accountId}_${userId}`;
    }

    function persist(cacheName, value) {
        storage.setItem(getUserCacheName(cacheName), JSON.stringify(value));
    }
    
    function load(cacheName) {
        const cache = storage.getItem(getUserCacheName(cacheName));
        const defaultValue = {};
        if (!cache) {
            persist(cacheName, defaultValue);
            return defaultValue;
        }
        
        return JSON.parse(storage.getItem(getUserCacheName(cacheName)));
    }
    
    service.clear = function clearCache() {
        Object.keys(storage).forEach(function(key) {
            if (key.indexOf(CACHE_PREFIX) === 0) {
                storage.removeItem(key);
            }
        });
    };
    
    service.invalidate = function invalidateCache(cacheName) {
        storage.removeItem(getUserCacheName(cacheName));
    };
    
    service.retrieve = function retrieveCacheItem(cacheName, key, promise, ttlInSeconds) {
        return Settings.get().then(() => {
            if (!Settings.FeatureFlagsInfo.EnableFrontEndCaching) {
                return promise();
            }
            
            const ttl = (ttlInSeconds || CACHE_TTL_IN_SECONDS) * 1000; // convert to ms
            const cacheKey = key || '_ALL_';
            
            let cachedValue = load(cacheName);
            if (cacheKey in cachedValue) {
                const currentTime = new Date().getTime();
    
                if (currentTime - cachedValue[cacheKey].expiresAt <= 0) {
                    return $q.when(cachedValue[cacheKey].value);
                }
            }
            
            return promise().then(function updateCache(result) {
                cachedValue = load(cacheName);
    
                cachedValue[cacheKey] = {
                    value: result,
                    expiresAt: new Date().getTime() + ttl
                };
    
                persist(cacheName, cachedValue);
    
                return result;
            });
        });
    };
            
    return service;
};

SessionStorageCacheService.$inject = ['$window', '$q', 'Settings'];
